<template>
  <div v-if="!isMobileView" class="aside">
    <h2 class="aside-title">Dashboards</h2>

    <section v-for="group in availableRoutesGroupedByOwner" :key="group.ownerId">
      <v-divider />
      <v-row
        class="aside-owner-section"
        align="center"
        no-gutters
        @click="handleExpandToggled(group.ownerId)"
      >
        <v-col>
          <vs-text>{{ group.ownerName }}</vs-text>
        </v-col>

        <v-col class="shrink">
          <v-btn
            small
            icon
            :disabled="group.ownerId === selectedOwnerId"
            @click.stop="handleExpandToggled(group.ownerId)"
          >
            <v-icon
              data-test="dashboard-group-expand-toggle"
              small
              :class="{
                'is-expanded': group.isExpanded || group.ownerId === selectedOwnerId,
              }"
              >expand_more
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-expand-transition>
        <div
          v-if="group.isExpanded || group.ownerId === selectedOwnerId"
          data-test="dashboard-items"
        >
          <router-link
            v-for="routeGroup in group.routes"
            :key="routeGroup.label"
            :to="routeGroup.to"
            active-class="is-active"
            class="aside-links"
            data-test="dashboard-link"
          >
            {{ routeGroup.label }}
          </router-link>
        </div>
      </v-expand-transition>
    </section>
    <v-divider />

    <vs-text style="padding-top: 13px" type="overline"><b>Need more analytics?</b></vs-text>
    <vs-text type="subdued"
      >Send us an email at
      <a href="mailto://support@ironsight.app?subject=Analytics request">support@ironsight.app</a>
    </vs-text>
  </div>
</template>

<script setup lang="ts">
import { isSmallScreenComposition } from '@/components/nav/mobileUtils'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router/composables'
import Dashboard from '@/models/dashboard'
import { groupBy, orderBy } from 'lodash'
import VsText from '@/components/vision/VsText.vue'
import { useScreen } from '@/common/useScreen'

const props = withDefaults(
  defineProps<{
    dashboards?: Dashboard[]
  }>(),
  {
    dashboards: () => [],
  },
)

const route = useRoute()
const { isMobileView } = useScreen()
const selectedDashboardId = computed(() => {
  if (route.name === 'new-metabase-dashboard') {
    return parseInt(route.params.id, 10)
  }
  return null
})
const selectedOwnerId = computed(
  () => props.dashboards?.find((d) => d.id === selectedDashboardId.value)?.ownerId,
)
const handleExpandToggled = (ownerId: string) => {
  const targetGroupIndex = availableRoutesGroupedByOwner.value.findIndex(
    (g) => g.ownerId === ownerId,
  )

  availableRoutesGroupedByOwner.value[targetGroupIndex].isExpanded =
    !availableRoutesGroupedByOwner.value[targetGroupIndex].isExpanded
}

watch(
  () => route,
  () => {
    availableRoutesGroupedByOwner.value.forEach((group) => {
      group.isExpanded = false
    })
  },
)

const availableRoutesGroupedByOwner = ref<
  {
    ownerId: string
    ownerName: string
    routes: {
      id: number
      name: string
      metabaseDashboardId: number
      ownerId: string
      ownerName: string
      label: string
      to: string
    }[]
    isExpanded: boolean
  }[]
>([])

watch(
  () => props.dashboards,
  (newDashboards) => {
    let dashboardsWithRoutes = newDashboards.map((dashboard) => ({
      label: dashboard.name,
      to: `/analytics/dashboards/${dashboard.id}`,
      ...dashboard,
    }))
    dashboardsWithRoutes = orderBy(dashboardsWithRoutes, (d) => d.label.toLowerCase())
    const groupedRoutesMap = groupBy(dashboardsWithRoutes, (d) => d.ownerId)
    const groupedRoutesArray = Object.keys(groupedRoutesMap).map((ownerId) => {
      return {
        ownerId,
        ownerName: groupedRoutesMap[ownerId][0].ownerName,
        routes: groupedRoutesMap[ownerId],
        isExpanded: false,
      }
    })
    availableRoutesGroupedByOwner.value = orderBy(groupedRoutesArray, (g) =>
      g.ownerName.toLowerCase(),
    )
  },
  { immediate: true },
)
</script>

<style scoped>
.aside {
  width: 200px;
  height: 100%;
  padding: var(--space-larger) var(--space-base);
  background-color: var(--color-silver);
  border-right: 1px solid var(--color-grey--lightest);
  overflow: auto;
}

.aside-title {
  font-weight: bold;
  font-size: 16px;
  margin-bottom: var(--space-base);
}

.aside-links {
  display: block;
  padding-bottom: 10px;
  padding-left: 15px;
  text-decoration: none;
  color: var(--color-grey);
  transition: all 200ms ease;
  font-family: var(--font-regular);
  font-size: 14px;
}

.aside-links:hover,
.is-active {
  color: var(--color-blue);
}

.aside-owner-section {
  padding: 7px 5px;
  cursor: pointer;
}

.is-expanded {
  transform: rotate(180deg);
}
</style>
